import { calculateProposalBudget, normalizeBudgetRates } from '@/lib/proposalBudget';
/**
 * Shared budget calculation logic for proposal views.
 * Used by Comparison and SignOff to ensure per-person estimates match exactly.
 */

interface VenueData {
  venue: any;
  shortlistItem: any;
}

interface DestinationContent {
  key_pros?: string[];
  considerations?: string[];
  images?: Array<{ url: string; caption?: string } | string>;
  flights_transportation?: number | null;
  food_beverage?: number | null;
  activities_meetings?: number | null;
  miscellaneous?: number | null;
}

/**
 * Calculate per-person cost for a venue using the same logic as Comparison tab.
 * Returns the per-person value (rounded) or null if cannot be calculated.
 */
export function calculateVenuePerPerson(
  hotelId: string,
  destinationId: string,
  budgetBreakdown: any,
  fullBudget: any,
  exchangeRates: any,
  groupSize: number,
  venueDataByDestination: Record<string, VenueData[]>,
  destinationContentByDestination: Record<string, DestinationContent>
): number | null {
  const hotel = { id: hotelId, destinationId };

  if (budgetBreakdown && fullBudget) {
    const breakdown = budgetBreakdown;
    const settings = fullBudget.settings;
    const nights = fullBudget?.nights ?? 0;
    const attendees = fullBudget?.attendees ?? groupSize;
    const eventCurrency = fullBudget?.base_currency || 'USD';
    const venueCurrency = breakdown?.otherCosts?.venue_currency || 'USD';

    const result = calculateProposalBudget({ breakdown, settings, nights, attendees,
      eventCurrency, exchangeRates: normalizeBudgetRates(exchangeRates) });
    return result.available ? Math.round(result.perAttendeeCost) : null;
  }

  // Fallback: simple calculation using destination content
  const destinationContent = destinationContentByDestination[destinationId];
  const venueData = venueDataByDestination[destinationId] || [];
  const venueInfo = venueData.find(v => v.venue.id === hotelId);

  let accommodation: number | null = null;
  if (venueInfo) {
    const totalCost = venueInfo.shortlistItem.override_total_cost ?? venueInfo.shortlistItem.cvent_total_cost;
    if (totalCost !== null && totalCost !== undefined) {
      accommodation = Math.round(totalCost);
    }
  }

  const foodAndBev = destinationContent?.food_beverage !== null && destinationContent?.food_beverage !== undefined
    ? Math.round(destinationContent.food_beverage / 100)
    : null;

  const activities = destinationContent?.activities_meetings !== null && destinationContent?.activities_meetings !== undefined
    ? Math.round(destinationContent.activities_meetings / 100)
    : null;

  const transport = destinationContent?.flights_transportation !== null && destinationContent?.flights_transportation !== undefined
    ? Math.round(destinationContent.flights_transportation / 100)
    : null;

  const misc = destinationContent?.miscellaneous !== null && destinationContent?.miscellaneous !== undefined
    ? Math.round(destinationContent.miscellaneous / 100)
    : null;

  const hasAllRequired = accommodation !== null && foodAndBev !== null && activities !== null && transport !== null && misc !== null;
  const totalEstimated = hasAllRequired && accommodation !== null
    ? accommodation + foodAndBev + activities + transport + misc
    : null;

  return hasAllRequired && totalEstimated !== null && groupSize > 0
    ? Math.round(totalEstimated / groupSize)
    : null;
}
